import { Inject, Injectable, InjectionToken } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { BeneficiaryDuplicateCheckDto } from '../../beneficiaries/beneficiary-duplicate-check/beneficiary-duplicate-check.dto';
import { SummaryExportEntryDto } from '../../beneficiaries/beneficiary-summary/beneficiary-summary-export/summary-export-entry.dto';
import { BeneficiaryDto } from '../../beneficiaries/dto/beneficiary.dto';
import { environment } from '../../environments/environment';
import { BeneficiaryFilter } from '../filter/beneficiaryFilter';
import { DisplayBeneficiaryDto } from '../models/display-beneficiary.dto';
import { PerimeterDto } from '../models/perimeter.dto';
import { StoredPropertyChange, PropertyChange } from '../models/property-change';
import { Pagination } from '../pagination/pagination';
import { PaginationOptions } from '../pagination/pagination-options';
import { ApiService, FileResponse } from './api.service';
import { FileService } from './file.service';

export const ACCESS_MODE_CHANGE: InjectionToken<StoredPropertyChange<string>> = new InjectionToken('__ACCESS_MODE');
export const PERIMETER_CHANGE: InjectionToken<StoredPropertyChange<PerimeterDto>> = new InjectionToken('__PERIMETER');

@Injectable({
  providedIn: 'root',
})
export class BeneficiariesService {
  public beneficiaryUpdate: Subject<BeneficiaryDto> = new Subject<BeneficiaryDto>();
  public beneficiaryUpdated$: Observable<BeneficiaryDto> = this.beneficiaryUpdate.asObservable();
  private readonly beneficiaryDelete: Subject<number> = new Subject<number>();
  public beneficiaryDeleted$: Observable<number> = this.beneficiaryDelete.asObservable();
  public readonly beneficiary: PropertyChange<BeneficiaryDto> = new PropertyChange();

  constructor(
    private readonly api: ApiService,
    private readonly fileService: FileService,
    @Inject(PERIMETER_CHANGE) public readonly perimeter: StoredPropertyChange<PerimeterDto>,
    @Inject(ACCESS_MODE_CHANGE) public readonly accessMode: StoredPropertyChange<string>,
  ) {
    accessMode.setValue('default');
  }

  public async findByFilter(beneficiaryFilter: BeneficiaryFilter, options: PaginationOptions): Promise<Pagination<DisplayBeneficiaryDto>> {
    return this.api.postPaginated<DisplayBeneficiaryDto>(options.buildUrl(environment.api.beneficiaries), beneficiaryFilter);
  }

  public async findById(idBeneficiary: number): Promise<BeneficiaryDto> {
    const uri: string = environment.api.beneficiaries + '/' + idBeneficiary + '/details';
    const result: BeneficiaryDto = await this.api.get<BeneficiaryDto>(uri);
    return BeneficiaryDto.fromJson(result);
  }

  public async updateBeneficiary(beneficiary: BeneficiaryDto): Promise<void> {
    await this.api.put(environment.api.beneficiaries, beneficiary);
    this.beneficiaryUpdate.next(undefined);
  }

  public async createBeneficiary(beneficiary: BeneficiaryDto): Promise<BeneficiaryDto> {
    const beneficiarySaved: BeneficiaryDto = await this.api.post(environment.api.beneficiaries + '/create', beneficiary);
    return BeneficiaryDto.fromJson(beneficiarySaved);
  }

  public async createBeneficiaryAsAdmin(beneficiary: BeneficiaryDto): Promise<BeneficiaryDto> {
    const uri: string = environment.api.beneficiaries + '/createAsAdmin';
    const beneficiarySaved: BeneficiaryDto = await this.api.post(uri, beneficiary);
    return BeneficiaryDto.fromJson(beneficiarySaved);
  }

  public async updateBeneficiaryAsAdmin(beneficiary: BeneficiaryDto): Promise<BeneficiaryDto> {
    const uri: string = environment.api.beneficiaries + '/updateAsAdmin';
    const beneficiarySaved: BeneficiaryDto = await this.api.post(uri, beneficiary);
    this.beneficiaryUpdate.next(beneficiarySaved);
    return BeneficiaryDto.fromJson(beneficiarySaved);
  }

  public async retrieveLast(beneficiaryIds: number[]): Promise<Pagination<DisplayBeneficiaryDto>> {
    return this.api.postPaginated(environment.api.beneficiaries + '/retrieveLast', beneficiaryIds);
  }

  public async deleteBeneficiary(beneficiaryId: number): Promise<void> {
    const uri: string = environment.api.beneficiaries + '/' + beneficiaryId;
    await this.api.delete(uri);
    this.beneficiaryDelete.next(beneficiaryId);
  }

  public async isAuthorized(beneficiaryId: number): Promise<boolean> {
    return this.api.get(environment.api.beneficiaries + `/${beneficiaryId}/${this.accessMode.value}/Authorize/Guard`);
  }

  public async getPerimeter(tab: string, accessMode?: string): Promise<PerimeterDto> {
    return this.api.get(environment.api.beneficiaries + `/perimeter/${tab}` + `${accessMode ? '/?accessMode=' + accessMode : ''}`);
  }

  public async exportSummary(beneficiaryId: number, summaryExportEntryDto: SummaryExportEntryDto): Promise<void> {
    const { filename, data }: FileResponse = await this.api.postWithFileResponse(
      environment.api.beneficiaries + '/' + beneficiaryId + '/summary',
      summaryExportEntryDto,
    );
    return this.fileService.saveAs(data, filename);
  }

  public async checkForDuplicate(dto: BeneficiaryDuplicateCheckDto): Promise<void> {
    await this.api.post<void, BeneficiaryDuplicateCheckDto>(environment.api.beneficiaries + '/duplicate-check', dto);
  }
}
